import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * The like / dislike / comments counter row of a roadmap card.
 *
 * Ports `roadmap_list_item.dart:145-235`: three tappable count groups
 * separated by thin vertical rules. The like icon (`like.png`) and dislike
 * icon (`dislike.png`) tint to the themed "active" color when the viewer has
 * liked / disliked the item; comments (`comment_lines.png`) is always neutral.
 *
 * Per the story's deferred-actions rule the taps surface the not-implemented
 * sheet via the parent's handlers; no like/dislike/comment logic lives here.
 */
interface RoadmapItemActionsRowProps {
  itemId: string;
  likes: number;
  dislikes: number;
  comments: number;
  liked: boolean;
  disliked: boolean;
  onLike: () => void;
  onDislike: () => void;
  onComments: () => void;
}

export const RoadmapItemActionsRow: React.FC<RoadmapItemActionsRowProps> = ({
  itemId,
  likes,
  dislikes,
  comments,
  liked,
  disliked,
  onLike,
  onDislike,
  onComments,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const iconSize = responsive.icon.small;

  const countStyle: React.CSSProperties = {
    color: theme.text.primary,
    fontSize: responsive.font.base,
  };

  const group: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: responsive.gap.xs,
    cursor: 'pointer',
  };

  const separator = (
    <div
      style={{
        width: responsive.border.thin,
        height: iconSize / 2,
        backgroundColor: theme.disabled,
        margin: `0 ${responsive.spacing.base}px`,
      }}
    />
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={group}
        onClick={onLike}
        {...qaAttr({ id: `roadmap-item-like-${itemId}` })}
      >
        <AssetIcon
          path="/assets/images/like.png"
          width={iconSize}
          height={iconSize}
          color={liked ? theme.blue : theme.white}
        />
        <span style={countStyle}>{likes}</span>
      </div>

      {separator}

      <div
        style={group}
        onClick={onDislike}
        {...qaAttr({ id: `roadmap-item-dislike-${itemId}` })}
      >
        <AssetIcon
          path="/assets/images/dislike.png"
          width={iconSize}
          height={iconSize}
          color={disliked ? theme.blue : theme.white}
        />
        <span style={countStyle}>{dislikes}</span>
      </div>

      {separator}

      <div
        style={group}
        onClick={onComments}
        {...qaAttr({ id: `roadmap-item-comments-${itemId}` })}
      >
        <AssetIcon
          path="/assets/images/comment_lines.png"
          width={iconSize}
          height={iconSize}
          color={theme.white}
        />
        <span style={countStyle}>{comments}</span>
      </div>
    </div>
  );
};
